/**
 * 모든 경로의 단일 출처 — 호출처는 이 객체로만 경로를 쓴다 (하드코딩 금지).
 * 도메인별로 묶어 합친다. 도메인 루트(`/auth`)는 상수로 두고 자식은 거기서 파생한다
 * (`${AUTH}/login`). 키는 도메인 접두어(`AUTH_LOGIN`). 동적 구간(`:param`)은 `buildPath`.
 */

const AUTH = '/auth';
const SUBSCRIPTIONS = '/subscriptions';
const AGREEMENTS = '/agreements';
const ERROR = '/error';
const STATS = '/stats';

const APP_ROUTES = {
  DASHBOARD: '/dashboard', // 대시보드(기본 착지점). 루트 '/' 는 라우트 트리 index 가 여기로 redirect.
  STATS_GENERATION: `${STATS}/generation`, // 발전통계 > 발전량 조회
  // ── 발전소 스코프 라우트 패턴 ────────────────────────────────────────────────────────────────────
  // 대시보드·발전량 조회·수익분석은 발전소 스코프를 **path 맨 뒤**로 운반한다(인버터 상세분석 정합). 세그먼트가
  // **선택적**(`:plantId?`)인 건 세 화면 모두 발전소 미지정 = **'전체' 스코프**라는 정상 상태가 있기 때문.
  // 조립은 `dashboardPath`·`generationPath`·`revenuePath`(선택적 segment 라 `buildPath` 대상 아님).
  //
  // **스코프별로 두 라우트로 쪼갠다**(대시보드·발전량 조회·수익분석 모두) — 단일은 전체에서 발전소를 골라
  // **들어간** 화면이라 뒤로가기가 필요한데, `isSubpage` 는 라우트별 선언이라 선택 세그먼트 **한** 라우트로는
  // 스코프별로 못 가른다(파생 금지 정책 — `routes/app.ts` 헤더). 그래서 단일 스코프만 `:plantId` **필수**
  // 세그먼트의 별도 라우트로 두고 `isSubpage: true` 를 단다(컴포넌트는 전체와 동일 — 화면이 아니라 위계 선언이
  // 갈리는 것뿐이다).
  DASHBOARD_SINGLE: '/dashboard/:plantId',
  STATS_GENERATION_SCOPED: `${STATS}/generation/:plantId`,
  REVENUE_SCOPED: '/revenue/:plantId',
  STATS_INVERTER: `${STATS}/inverter`, // 발전통계 > 인버터 상세분석
  // 인버터 상세분석 드릴인(탭 + 발전소). 조립은 `inverterAnalysisPath`(탭 선택적) — 이 상수는 **라우트 정의·위계
  // 판정용 패턴**이라 `buildPath` 대상이 아니다(선택 segment `:tab?`).
  STATS_INVERTER_DETAIL: `${STATS}/inverter/:tab?/:plantId`,
  REVENUE: '/revenue', // 수익분석
  ALERTS: '/alerts', // 이상알림
  // 구 마이페이지 — 설정 '내정보' 탭으로 흡수. 구앱 딥링크·북마크 흡수를 위해 라우트만 남기고 redirect.
  MY: '/my',
  PLANTS: '/plants', // 설비정보 (발전소 목록) — 네비 최상위 단일 메뉴
  PLANT_INFO_DETAIL: '/plants/:plantId', // 발전소 정보 상세 (기본/설치/계약) — 식별자 맨 뒤
  // 인버터 구성 (정보 상세 설치정보의 [자세히]로 진입) — 정보 상세의 **하위 페이지**라 식별자 뒤에 세그먼트를
  // 더 붙인다('<' 는 마지막 세그먼트를 떼 정보 상세로 올라간다). 구 인버터 상세분석 '구성' 탭을 여기로 옮겼다 —
  // 인버터 제원은 분석(조회조건·기간)이 아니라 **설비 정보**라 설비정보 위계에 속한다.
  PLANT_INVERTERS: '/plants/:plantId/inverters',
  PLANT_USE_SETUP: '/plants/:plantId/use', // 발전소 용도/계약 설정·변경 (정보 상세에서 진입)
  SUPPORT: '/support', // 고객센터
  SETTINGS: '/settings', // 설정 — 탭 base. 자체 렌더 없이 기본 탭으로 redirect(canonical URL 1탭 1경로)
  // 설정 탭(내정보·환경설정) — 탭을 **path segment** 로 운반(인버터 상세분석 정합). 탭 전환은 같은 화면 안의
  // 이동이라 `isSubpage: false`. 조립은 `settingsPath(tab)`.
  SETTINGS_TAB: '/settings/:tab',
} as const;

const AUTH_ROUTES = {
  AUTH,
  AUTH_LOGIN: `${AUTH}/login`,
  AUTH_CONSENT: `${AUTH}/consent`,
  AUTH_PASSWORD: `${AUTH}/password`,
} as const;

const PERMISSION_ROUTES = {
  PERMISSION: '/permission',
} as const;

/**
 * 강제 업데이트 — `/auth` 와 **같은 최상위 레벨**(앱 본체 밖). 하위호환이 끊긴 구버전은 어떤 화면도
 * 신뢰할 수 없어 본체 라우트 아래 두지 않는다. native 전용 도달점(web 은 새로고침 다이얼로그로 해소).
 */
const UPDATE_ROUTES = {
  UPDATE_REQUIRED: '/update-required',
} as const;

const SUBSCRIPTION_ROUTES = {
  SUBSCRIPTIONS, // 발전소 목록 = `/subscriptions`(인덱스). (구 `/subscriptions/list` 폐지)
  // 구독유형 선택 — 발전소를 **path param**(`:plantId`)으로. nav 는 `buildPath(SUBSCRIPTIONS_PLAN, { plantId })`.
  // (구 `/subscriptions/plan?plantId=` 폐지). static `result`·`callback` 보다 매칭 우선순위 낮음(RR static>dynamic).
  SUBSCRIPTIONS_PLAN: `${SUBSCRIPTIONS}/:plantId`,
  // Toss 카드 등록 인증창 콜백 목적지. `:result` = success | fail 단일 동적 라우트
  // (`buildPath` 로 분기). 컨텍스트(연동종류·복귀경로)는 `?state=`(인코딩)로 전달.
  // 성공 시 authKey·customerKey 쿼리 수신. 핸들러(빌링키 발급 POST /billings)는 후속.
  SUBSCRIPTIONS_CALLBACK: `${SUBSCRIPTIONS}/callback/:result`,
  // 결제 결과 화면(완료/실패). 메시지·구분은 `?state=`(`ResultState` 인코딩)로 전달.
  SUBSCRIPTIONS_RESULT: `${SUBSCRIPTIONS}/result`,
} as const;

/** 약관 코드: terms | privacy | refund (BE agreementType 정합). */
const AGREEMENT_ROUTES = {
  AGREEMENTS,
  AGREEMENT_DETAIL: `${AGREEMENTS}/:code`,
} as const;

/**
 * 공개 고객지원 — 스토어(App Store Connect · Play Console) 제출 **Support URL** 목적지.
 * 앱 본체 `/support`(AppLayout 하위 = requireAuth)와 별개다: 심사자는 로그인 없이 열기 때문에
 * 인증 화면을 심사 표면으로 쓸 수 없다.
 */
const HELP_ROUTES = {
  HELP: '/help',
} as const;

/**
 * 앱 열기 폴백 — `/open/*`. **앱으로 보낼 링크의 겉면**이다.
 *
 * 외부(문자·메일·QR)로 뿌리는 링크를 커스텀 스킴(`heviton://`)으로 직접 주면 **미설치 기기에서 아무 일도
 * 일어나지 않는다**(브라우저가 처리기를 못 찾는다). 대신 이 웹 경로를 뿌리면 어디서 열든 화면이 뜨고,
 * 거기서 앱 열기·설치를 고르게 할 수 있다.
 *
 * 유니버설 링크를 붙이면 **앱 설치자는 이 페이지를 건너뛰고** 앱이 바로 열린다(미설치자만 여기 도달).
 * 즉 이 경로는 유니버설 링크의 폴백 자리를 미리 만들어 두는 것이기도 하다.
 *
 * 뒤에 목적지 경로를 그대로 잇는다 — `/open/alerts?plantId=3` → 앱·웹 모두 `/alerts?plantId=3`.
 */
const OPEN_ROUTES = {
  OPEN: '/open',
  OPEN_TARGET: '/open/*',
} as const;

const ERROR_ROUTES = {
  ERROR,
  ERROR_MAINTENANCE: `${ERROR}/maintenance`,
  ERROR_UNAUTHORIZED: `${ERROR}/unauthorized`,
} as const;

export const ROUTES = {
  ...APP_ROUTES,
  ...AUTH_ROUTES,
  ...PERMISSION_ROUTES,
  ...UPDATE_ROUTES,
  ...SUBSCRIPTION_ROUTES,
  ...AGREEMENT_ROUTES,
  ...HELP_ROUTES,
  ...OPEN_ROUTES,
  ...ERROR_ROUTES,
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];

/** 패턴의 `:param` 들을 타입 키로 추출 (`/a/:x/b/:y` → `'x' | 'y'`). */
type ExtractParams<T extends string> = T extends `${string}:${infer P}/${infer Rest}`
  ? P | ExtractParams<`/${Rest}`>
  : T extends `${string}:${infer P}`
    ? P
    : never;

/**
 * 동적 경로 바인딩. 누락 키는 throw(빈 구간 사고 방지), 값은 `encodeURIComponent`.
 * 예: `buildPath(ROUTES.AGREEMENT_DETAIL, { code: 'terms' })` → `/agreements/terms`.
 */
export function buildPath<T extends string>(
  pattern: T,
  params: Record<ExtractParams<T>, string>,
): string {
  return pattern.replace(/:([a-zA-Z][a-zA-Z0-9_]*)/g, (_, key: string) => {
    const value = (params as Record<string, string>)[key];
    if (value === undefined) {
      throw new Error(`buildPath: missing param "${key}" for pattern "${pattern}"`);
    }
    return encodeURIComponent(value);
  });
}

/**
 * 인버터 상세분석 드릴인 path — **탭 + 발전소(정체성)를 path segment, 식별자(plantId)를 맨 뒤**로
 * (`/stats/inverter/:tab?/:plantId`, admin plant-detail 정합). 탭 없으면 `/stats/inverter/:plantId`(단일 세그=plantId,
 * RR 실측 확인). 인버터 분석은 "전체 발전소" 없어 무플랜트 진입은 firstPlant materialize. rtuId·필터는 URL 미운반.
 */
export const inverterAnalysisPath = (plantId: string, tab?: string): string =>
  `${ROUTES.STATS_INVERTER}/${tab ? `${tab}/` : ''}${encodeURIComponent(plantId)}`;

/**
 * 발전소 스코프 path 조립(대시보드·발전량 조회) — 발전소 지정이면 `<base>/<plantId>`, **미지정(전체)이면 base**.
 * `null`·`undefined` 를 모두 전체로 받아 호출처가 "전체 = 값 없음" 을 그대로 넘길 수 있다.
 */
const scopedPath = (base: string, plantId?: string | null): string =>
  plantId ? `${base}/${encodeURIComponent(plantId)}` : base;

/** 대시보드 path — 전체 `/dashboard` · 단일 `/dashboard/<plantId>`. */
export const dashboardPath = (plantId?: string | null): string =>
  scopedPath(ROUTES.DASHBOARD, plantId);

/** 발전량 조회 path — 전체 `/stats/generation` · 단일 `/stats/generation/<plantId>`. */
export const generationPath = (plantId?: string | null): string =>
  scopedPath(ROUTES.STATS_GENERATION, plantId);

/** 수익분석 path — 전체 `/revenue` · 단일 `/revenue/<plantId>`. */
export const revenuePath = (plantId?: string | null): string => scopedPath(ROUTES.REVENUE, plantId);

/** 발전소 정보 상세 path — `/plants/:plantId`(식별자 맨 뒤). 목록·수익·대시보드 팝업의 단일 진입. */
export const plantInfoPath = (plantId: string): string =>
  `${ROUTES.PLANTS}/${encodeURIComponent(plantId)}`;

/**
 * 설정 탭 path — `/settings/<tab>`. 탭 값 목록은 `pages/settings` 가 소유하고 여기선 **기본 탭**만 안다
 * (base 진입 materialize · 구 `/my` redirect 목적지 — 두 곳이 같은 기본값을 쓰도록).
 */
export const settingsPath = (tab: string = 'my'): string => `${ROUTES.SETTINGS}/${tab}`;

/** 인버터 구성 path — `/plants/:plantId/inverters`. 정보 상세 설치정보 [자세히]·대시보드 인버터 구성 카드에서 진입. */
export const plantInvertersPath = (plantId: string): string =>
  `${ROUTES.PLANTS}/${encodeURIComponent(plantId)}/inverters`;

/** 발전소 용도/계약 설정·변경 path — `/plants/:plantId/use`. 정보 상세 액션에서 진입, 저장 후 복귀. */
export const plantUseSetupPath = (plantId: string): string =>
  `${ROUTES.PLANTS}/${encodeURIComponent(plantId)}/use`;
